import { cn } from '@/shared/lib/utils'

function ShimmerBlock({ className }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-lg bg-gradient-to-r from-slate-100 via-slate-200/80 to-slate-100 bg-[length:200%_100%]',
        className,
      )}
    />
  )
}

function CardShell({ children, className }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-black/[0.06] bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)]',
        className,
      )}
    >
      {children}
    </div>
  )
}

export default function DashboardShimmer() {
  return (
    <div
      className="flex flex-col gap-4 sm:gap-5"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex justify-end">
        <ShimmerBlock className="h-9 w-28 rounded-full" />
      </div>

      <ShimmerBlock className="h-[108px] w-full rounded-[24px] sm:h-[120px]" />

      <CardShell>
        <ShimmerBlock className="mb-2 h-4 w-52" />
        <ShimmerBlock className="mb-4 h-3 w-72 max-w-full" />
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="w-[200px] shrink-0 rounded-2xl border border-slate-100 bg-slate-50/80 p-4"
            >
              <div className="mb-3 flex justify-between gap-2">
                <ShimmerBlock className="h-3.5 w-24" />
                <ShimmerBlock className="h-3 w-12" />
              </div>
              <div className="space-y-2.5">
                {Array.from({ length: 5 }).map((__, row) => (
                  <div key={row} className="flex justify-between gap-2">
                    <ShimmerBlock className="h-3 w-20" />
                    <ShimmerBlock className="h-3 w-6" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardShell>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <CardShell key={index}>
            <ShimmerBlock className="h-3.5 w-28" />
            <ShimmerBlock className="mt-4 h-10 w-16" />
            <ShimmerBlock className="mt-2 h-3 w-36" />
            <div className="mt-5 space-y-3">
              {Array.from({ length: 3 }).map((__, row) => (
                <div key={row} className="space-y-1.5">
                  <div className="flex justify-between">
                    <ShimmerBlock className="h-3 w-24" />
                    <ShimmerBlock className="h-3 w-10" />
                  </div>
                  <ShimmerBlock className="h-1.5 w-full rounded-full" />
                </div>
              ))}
            </div>
          </CardShell>
        ))}
      </div>

      <div className="grid items-stretch gap-4 xl:grid-cols-[340px_1fr]">
        <CardShell>
          <ShimmerBlock className="h-4 w-36" />
          <ShimmerBlock className="mt-2 h-3 w-24" />
          <div className="mt-4 flex gap-2">
            <ShimmerBlock className="h-7 w-16 rounded-full" />
            <ShimmerBlock className="h-7 w-16 rounded-full" />
            <ShimmerBlock className="h-7 w-20 rounded-full" />
          </div>
          <div className="mt-5 space-y-4">
            {Array.from({ length: 4 }).map((_, row) => (
              <div key={row} className="space-y-1.5">
                <div className="flex justify-between">
                  <ShimmerBlock className="h-3 w-20" />
                  <ShimmerBlock className="h-3 w-12" />
                </div>
                <ShimmerBlock className="h-1.5 w-full rounded-full" />
              </div>
            ))}
          </div>
        </CardShell>

        <CardShell className="border-amber-100 bg-amber-50/40">
          <div className="flex items-center gap-2">
            <ShimmerBlock className="h-3 w-3 rounded-full" />
            <ShimmerBlock className="h-4 w-40" />
          </div>
          <div className="mt-4 space-y-3">
            {Array.from({ length: 3 }).map((_, row) => (
              <div
                key={row}
                className="rounded-2xl border border-white/70 bg-white/80 p-4"
              >
                <div className="flex gap-3">
                  <ShimmerBlock className="size-9 shrink-0 rounded-full" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <ShimmerBlock className="h-3.5 w-40 max-w-full" />
                    <ShimmerBlock className="h-3 w-56 max-w-full" />
                    <div className="flex gap-2 pt-1">
                      <ShimmerBlock className="h-6 w-20 rounded-full" />
                      <ShimmerBlock className="h-6 w-20 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardShell>
      </div>
    </div>
  )
}
